import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';

Vue.use(Vuex);

// ─── Helper : clé localStorage propre à chaque utilisateur ──────────────────
// On prend les 16 derniers caractères du token pour ne pas exposer le token
// complet en clair dans les clés localStorage.
function bagKey(token) {
    if (!token) return null;
    return 'shopping_bag_' + token.slice(-16);
}

function saveBag(token, bag) {
    const key = bagKey(token);
    if (key) localStorage.setItem(key, JSON.stringify(bag));
}

function loadBag(token) {
    const key = bagKey(token);
    if (!key) return { items: [] };
    try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : { items: [] };
    } catch {
        return { items: [] };
    }
}

function removeBag(token) {
    const key = bagKey(token);
    if (key) localStorage.removeItem(key);
}
// ────────────────────────────────────────────────────────────────────────────

export default new Vuex.Store({
    state: {
        otpVerified: null,
        error: null,
        loading: false,
        verificationComplete: false,
        user_notifications: [],
        isVerified: false,
        username: "",
        email: '',
        close_url: "/All",
        close_url2: "/business-account/dashboard",
        close_url3: "/",
        save_item: "",
        current_app_feature: "",
        userData: null,
        user_info_state: false,
        user_info: { user: {} },
        unread_user_notifications: 0,
        isAuthenticated: false,
        lengthActiveCampaigns: 0,
        lengthActiveCampaigns2: 0,
        lengthActiveLoans: 0,
        token: '',
        isLoading: false,
        snackBar: false,
        snackBarColor: "",
        snackBarMessage: "",
        snackBarTimeOut: 4000,
        acceptedNjangiGroup: false,
        acceptedNjangiGroupId: '',
        acceptedNjangiEvent: false,
        acceptedNjangiEventId: '',
        MerchantCodeMTN: '024781',
        MerchantCodeOrange: '778810',
        paymentStatus: false,
        isMarketer: false,
        businessAccount: false,
        dialog: false,
        dialog_title: "",
        dialog_message: "",
        // Le panier est maintenant isolé par utilisateur via localStorage
        shopping_bag: { items: [] },
        showChannelInfoPopup: false,
        channelInfoPopupDismissed: false,
        njangiWelcomeVersion: 1,
        njangiWelcomeSeenUsers: {},
        loanPromoVersion: 1,
        loanPromoSeenUsers: {},
        referralEarningsVersion: 1,
        referralEarningsSeenUsers: {},
        kycPromptVersion: 1,
        kycPromptSeenUsers: {},
        paymentVerificationVisible: false,
        paymentVerificationData: {
            amount: "",
            operator: "",
            phoneNumber: "",
            feature: "",
            id: "",
        },
        webpushSeenUsers: {},
        webpushVersion: 1,
    },
    mutations: {
        initialiseStore(state) {
            // ── Authentification ────────────────────────────────────────────
            if (localStorage.getItem('token')) {
                state.token = localStorage.getItem('token');
                state.isAuthenticated = true;
            } else {
                state.token = '';
                state.isAuthenticated = false;
            }

            // ── Panier : chargé uniquement pour l'utilisateur connecté ──────
            // Si aucun token → panier vide en mémoire, rien de l'ancien user.
            state.shopping_bag = loadBag(state.token);

            // ── Popups (inchangés) ──────────────────────────────────────────
            const popupKeys = [
                ['njangi_welcome_seen_users',    'njangiWelcomeSeenUsers'],
                ['loan_promo_seen_users',         'loanPromoSeenUsers'],
                ['referral_earnings_seen_users',  'referralEarningsSeenUsers'],
                ['kyc_prompt_seen_users',         'kycPromptSeenUsers'],
                ['webpush_seen_users',            'webpushSeenUsers'],
            ];

            popupKeys.forEach(([lsKey, stateKey]) => {
                const raw = localStorage.getItem(lsKey);
                if (!raw) { state[stateKey] = {}; return; }
                try {
                    const saved = JSON.parse(raw);
                    if (typeof saved !== 'object' || saved === null) throw new Error('bad format');
                    // Migration : anciens boolean → objets
                    const migrated = {};
                    Object.keys(saved).forEach(uid => {
                        const v = saved[uid];
                        migrated[uid] = typeof v === 'boolean'
                            ? { seen: v, version: 1, timestamp: new Date().toISOString() }
                            : v;
                    });
                    state[stateKey] = migrated;
                } catch (e) {
                    console.error(`Error parsing ${lsKey}:`, e);
                    state[stateKey] = {};
                }
            });
        },

        // ── Panier : ajout ─────────────────────────────────────────────────
        addToShoppingBag(state, item) {
            const exists = state.shopping_bag.items.find(i => i.product.id === item.product.id);
            if (exists) {
                exists.quantity = parseInt(exists.quantity) + parseInt(item.quantity);
            } else {
                state.shopping_bag.items.push(item);
            }
            // Sauvegarde dans la clé propre à cet utilisateur
            saveBag(state.token, state.shopping_bag);
        },

        // ── Panier : reset ─────────────────────────────────────────────────
        resetShoppingBag(state) {
            state.shopping_bag = { items: [] };
            // Efface uniquement la clé de cet utilisateur
            removeBag(state.token);
        },

        // ── Connexion : charger le panier de CE nouvel utilisateur ─────────
        setToken(state, token) {
            state.token = token;
            state.isAuthenticated = true;
            // On charge le panier qui appartient à ce token (vide si première connexion)
            state.shopping_bag = loadBag(token);
        },

        // ── Déconnexion : vider le panier en mémoire (pas en localStorage) ─
        // Le panier reste sauvegardé en localStorage sous la clé de l'utilisateur
        // pour être restauré à sa prochaine connexion sur ce navigateur.
        removeToken(state) {
            state.token = "";
            state.isAuthenticated = false;
            // On vide le panier en mémoire — le prochain utilisateur part à zéro
            state.shopping_bag = { items: [] };
        },

        // ───────────────────────────────────────────────────────────────────
        // Toutes les autres mutations sont inchangées
        // ───────────────────────────────────────────────────────────────────

        setNjangiWelcomeSeenForUser(state, { userId }) {
            state.njangiWelcomeSeenUsers[userId] = {
                seen: true, version: state.njangiWelcomeVersion, timestamp: new Date().toISOString()
            };
            localStorage.setItem('njangi_welcome_seen_users', JSON.stringify(state.njangiWelcomeSeenUsers));
        },
        incrementNjangiWelcomeVersion(state) { state.njangiWelcomeVersion += 1; },
        setNjangiWelcomeVersion(state, version) { state.njangiWelcomeVersion = version; },

        setLoanPromoSeenForUser(state, { userId }) {
            state.loanPromoSeenUsers[userId] = {
                seen: true, version: state.loanPromoVersion, timestamp: new Date().toISOString()
            };
            localStorage.setItem('loan_promo_seen_users', JSON.stringify(state.loanPromoSeenUsers));
        },
        incrementLoanPromoVersion(state) { state.loanPromoVersion += 1; },
        setLoanPromoVersion(state, version) { state.loanPromoVersion = version; },

        setReferralEarningsSeenForUser(state, { userId }) {
            state.referralEarningsSeenUsers[userId] = {
                seen: true, version: state.referralEarningsVersion, timestamp: new Date().toISOString()
            };
            localStorage.setItem('referral_earnings_seen_users', JSON.stringify(state.referralEarningsSeenUsers));
        },
        incrementReferralEarningsVersion(state) { state.referralEarningsVersion += 1; },
        setReferralEarningsVersion(state, version) { state.referralEarningsVersion = version; },

        setKycPromptSeenForUser(state, { userId }) {
            Vue.set(state.kycPromptSeenUsers, userId, {
                seen: true, version: state.kycPromptVersion, timestamp: new Date().toISOString()
            });
            localStorage.setItem('kyc_prompt_seen_users', JSON.stringify(state.kycPromptSeenUsers));
        },
        incrementKycPromptVersion(state) { state.kycPromptVersion += 1; },
        setKycPromptVersion(state, version) { state.kycPromptVersion = version; },

        setWebpushSeenForUser(state, { userId }) {
            state.webpushSeenUsers[userId] = {
                seen: true, version: state.webpushVersion, timestamp: new Date().toISOString()
            };
            localStorage.setItem('webpush_seen_users', JSON.stringify(state.webpushSeenUsers));
        },
        incrementWebpushVersion(state) { state.webpushVersion += 1; },
        setWebpushVersion(state, version) { state.webpushVersion = version; },

        setPaymentStatus(state, status) { state.paymentStatus = status; },
        setIsMarketer(state, status) { state.isMarketer = status; },
        setBusinessAccount(state, status) { state.businessAccount = status; },
        setOtpStatus(state, otpStatus) { state.otpVerified = otpStatus; },
        setOtpLoading(state, isLoading) { state.loading = isLoading; },
        setOtpError(state, errorMessage) { state.error = errorMessage; },
        SET_EMAIL(state, email) { state.email = email; },
        setIsVerified(state, value) { state.isVerified = value; },
        SET_VERIFICATION_STATUS(state, status) { state.isVerified = status; },

        setUserName(state, username) { state.username = username; },
        setUser(state, user) { state.user = user; },
        setUserState(state, user_state) { state.user_info_state = user_state; },
        setUserInfo(state, user_info) { state.user_info = user_info; },
        setAppFeature(state, feature) { state.current_app_feature = feature; },
        setIsLoading(state, status) { state.isLoading = status; },
        setLengthActiveCampaigns(state, length) { state.lengthActiveCampaigns = length; },
        setLengthActiveCampaigns2(state, length) { state.lengthActiveCampaigns2 = length; },
        setLengthActiveLoans(state, length) { state.lengthActiveLoans = length; },
        setCloseURL(state, url) { state.close_url = url; },
        setCloseURL2(state, url) { state.close_url2 = url; },
        setCloseURL3(state, url) { state.close_url3 = url; },
        setSaveItem(state, item_id) { state.save_item = item_id; },

        acceptedNjangiGroupInvitation(state, group_id) {
            state.acceptedNjangiGroup = true; state.acceptedNjangiGroupId = group_id;
        },
        acceptedNjangiEventInvitation(state, event_id) {
            state.acceptedNjangiEvent = true; state.acceptedNjangiEventId = event_id;
        },
        clearNjangiGroupInvitation(state) {
            state.acceptedNjangiGroup = false; state.acceptedNjangiGroupId = '';
        },
        clearNjangiEventInvitation(state) {
            state.acceptedNjangiEvent = false; state.acceptedNjangiEventId = '';
        },

        activateDialog(state, status) { state.dialog = status; },
        setDialogTitle(state, title) { state.dialog_title = title; },
        setDialogMessage(state, message) { state.dialog_message = message; },
        activateSnackBar(state, status) { state.snackBar = status; },
        setSnackBarMessage(state, message) { state.snackBarMessage = message; },
        setSnackBarColor(state, color) { state.snackBarColor = color; },
        setSnackBarTimeOut(state, timeout) { state.snackBarTimeOut = timeout; },

        SET_USER_NOTIFICATIONS(state, notifications) { state.user_notifications = notifications; },
        setVerificationComplete(state, status) { state.verificationComplete = status; },
        setUnreadUserNotifications(state) {
            state.unread_user_notifications = state.user_notifications.filter(n => !n.read).length;
        },

        showPaymentVerification(state, { amount, operator, phoneNumber, feature, id }) {
            state.paymentVerificationData = { amount, operator, phoneNumber, feature, id };
            state.paymentVerificationVisible = true;
            localStorage.setItem('paymentVerificationData', JSON.stringify(state.paymentVerificationData));
        },
        hidePaymentVerification(state) {
            state.paymentVerificationVisible = false;
            state.paymentVerificationData = { amount: "", operator: "", phoneNumber: "", feature: "", id: "" };
            localStorage.removeItem('paymentVerificationData');
        },
        restorePaymentVerification(state) {
            const savedData = localStorage.getItem('paymentVerificationData');
            if (savedData) {
                try {
                    state.paymentVerificationData = JSON.parse(savedData);
                    state.paymentVerificationVisible = true;
                } catch (error) {
                    console.error('Error restoring payment verification:', error);
                    localStorage.removeItem('paymentVerificationData');
                }
            }
        },
    },
    actions: {
        updateEmail({ commit }, email) { commit('SET_EMAIL', email); },
        updateVerificationStatus({ commit }, status) { commit('SET_VERIFICATION_STATUS', status); },
        setEmail({ commit }, email) { commit('SET_EMAIL', email); },
        updateIsVerified({ commit }, value) { commit('setIsVerified', value); },

        fetchUserNotifications({ commit }) {
            axios.get('/api/v1/home/get/user/notifications/')
                .then(response => commit('SET_USER_NOTIFICATIONS', response.data))
                .catch(console.error);
        },

        async checkPaymentStatus({ commit }) {
            try {
                const response = await axios.get('/api/v1/home/payment/');
                if (response.data && typeof response.data.payment_status !== 'undefined') {
                    commit('setPaymentStatus', response.data.payment_status);
                    return response.data.payment_status;
                }
                return false;
            } catch { return false; }
        },

        showSnackBar({ commit, state }, { message, color, timeout = 4000 }) {
            commit('setSnackBarMessage', message);
            commit('setSnackBarColor', color);
            if (timeout) commit('setSnackBarTimeOut', timeout);
            if (state.snackBar) {
                commit('activateSnackBar', false);
                setTimeout(() => commit('activateSnackBar', true), 100);
            } else {
                commit('activateSnackBar', true);
            }
        },

        async loadPartnerEligibility({ commit }) {
            try {
                const [mkRes, profRes] = await Promise.all([
                    axios.get('/api/v1/home/check_marketer/'),
                    axios.get('/api/v1/home/user/profile/')
                ]);

                const isMarketer = !!(mkRes.data && mkRes.data.is_marketer);
                commit('setIsMarketer', isMarketer);

                const businessAccount = !!(profRes.data && profRes.data.business_account);
                commit('setBusinessAccount', businessAccount);

                let paymentStatus = false;
                if (profRes.data && typeof profRes.data.payment_status !== 'undefined') {
                    paymentStatus = !!profRes.data.payment_status;
                    commit('setPaymentStatus', paymentStatus);
                }

                return isMarketer || (businessAccount && paymentStatus);
            } catch (error) {
                console.error('loadPartnerEligibility error:', error);
                return false;
            }
        },

        setCloseURLWithQueryParams({ commit }, { baseUrl, closeUrlIndex = 1 }) {
            const currentRoute = this._vm.$route;
            const ref = currentRoute.query.ref || currentRoute.query.referral || '';
            let urlWithParams = baseUrl;
            if (ref) {
                const separator = baseUrl.includes('?') ? '&' : '?';
                urlWithParams = `${baseUrl}${separator}ref=${ref}`;
            }
            if (closeUrlIndex === 1) commit('setCloseURL', urlWithParams);
            else if (closeUrlIndex === 2) commit('setCloseURL2', urlWithParams);
            else if (closeUrlIndex === 3) commit('setCloseURL3', urlWithParams);
        },

        incrementWelcomeVersion({ commit }) { commit('incrementNjangiWelcomeVersion'); },
        incrementLoanPromoVersion({ commit }) { commit('incrementLoanPromoVersion'); },
        incrementReferralEarningsVersion({ commit }) { commit('incrementReferralEarningsVersion'); },
        incrementKycPromptVersion({ commit }) { commit('incrementKycPromptVersion'); },
        incrementWebpushVersion({ commit }) { commit('incrementWebpushVersion'); },
    },
    getters: {
        isVerified: s => s.isVerified,
        verificationComplete: s => s.verificationComplete,
        getEmail: s => s.email,
        otpVerified: s => s.otpVerified,
        otpLoading: s => s.loading,
        otpError: s => s.error,
        paymentStatus: s => s.paymentStatus,

        // ── Panier ─────────────────────────────────────────────────────────
        // Le badge compte uniquement les articles du state (déjà isolé par user)
        shoppingBagCount: s => s.shopping_bag.items.reduce((acc, i) => acc + parseInt(i.quantity || 0), 0),

        njangiWelcomeSeenForCurrentUser: (state) => {
            const userId = state.user_info?.user?.id || state.username || 'anonymous';
            const d = state.njangiWelcomeSeenUsers[userId];
            if (!d) return false;
            return d.seen === true && d.version >= state.njangiWelcomeVersion;
        },
        njangiWelcomeCurrentVersion: s => s.njangiWelcomeVersion,

        loanPromoSeenForCurrentUser: (state) => {
            const userId = state.user_info?.user?.id || state.username || 'anonymous';
            const d = state.loanPromoSeenUsers[userId];
            if (!d) return false;
            return d.seen === true && d.version >= state.loanPromoVersion;
        },
        loanPromoCurrentVersion: s => s.loanPromoVersion,

        referralEarningsSeenForCurrentUser: (state) => {
            const userId = state.user_info?.user?.id || state.username || 'anonymous';
            const d = state.referralEarningsSeenUsers[userId];
            if (!d) return false;
            return d.seen === true && d.version >= state.referralEarningsVersion;
        },
        referralEarningsCurrentVersion: s => s.referralEarningsVersion,

        kycPromptSeenForCurrentUser: (state) => {
            const userId = state.user_info?.user?.id || state.username || 'anonymous';
            const d = state.kycPromptSeenUsers[userId];
            if (!d) return false;
            return d.seen === true && d.version >= state.kycPromptVersion;
        },
        kycPromptCurrentVersion: s => s.kycPromptVersion,

        userId: s => s.user_info?.user?.id || s.username || 'anonymous',

        webpushSeenForCurrentUser: (state) => {
            const userId = state.user_info?.user?.id || state.username || 'anonymous';
            const d = state.webpushSeenUsers[userId];
            if (!d) return false;
            return d.seen === true && d.version >= state.webpushVersion;
        },
        webpushCurrentVersion: s => s.webpushVersion,
    },
});